import { createContext, useContext, useEffect, useState, useMemo } from "react";
import api from "../lib/api";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [realUser, setRealUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [viewMode, setViewModeState] = useState(() => {
    return localStorage.getItem("idg_emulated_view_mode") || "admin";
  });

  const setViewMode = (mode) => {
    setViewModeState(mode);
    localStorage.setItem("idg_emulated_view_mode", mode);
  };

  useEffect(() => {
    const token = localStorage.getItem("tf_token") || sessionStorage.getItem("tf_token");
    if (!token) {
      setLoading(false);
      return;
    }
    api.get("/auth/me")
      .then((r) => setRealUser(r.data))
      .catch(() => {
        localStorage.removeItem("tf_token");
        sessionStorage.removeItem("tf_token");
      })
      .finally(() => setLoading(false));
  }, []);

  const login = async (email, password, rememberMe = true) => {
    const { data } = await api.post("/auth/login", { email, password, username: email });
    const token = data.token || data.access_token;
    if (rememberMe) {
      localStorage.setItem("tf_token", token);
    } else {
      sessionStorage.setItem("tf_token", token);
    }
    setRealUser(data.user);
    return data.user;
  };

  const register = async (payload) => {
    const { data } = await api.post("/auth/register", payload);
    const token = data.token || data.access_token;
    localStorage.setItem("tf_token", token);
    setRealUser(data.user);
    return data.user;
  };

  const logout = () => {
    localStorage.removeItem("tf_token");
    sessionStorage.removeItem("tf_token");
    setRealUser(null);
    setViewMode("admin");
  };

  const refresh = async () => {
    const { data } = await api.get("/auth/me");
    setRealUser(data);
    return data;
  };

  const setUser = (u) => {
    setRealUser(u);
  };

  // Compute effective user based on view mode (only applies to staff: root/admin)
  const isStaff = realUser && ["root", "admin"].includes(realUser.role);
  
  const user = useMemo(() => {
    if (!realUser) return null;
    if (!isStaff || viewMode === "admin") return realUser;
    
    if (viewMode === "guest") {
      return null;
    }

    if (viewMode === "member") {
      return {
        ...realUser,
        role: "user",
        group_name: "Membru",
        group_color: "#94a3b8",
        is_vip: false,
        vip_tier: null,
        is_emulated: true,
      };
    }

    return realUser;
  }, [realUser, isStaff, viewMode]);

  const isEmulating = Boolean(isStaff && viewMode !== "admin");

  return (
    <AuthContext.Provider 
      value={{ 
        user, 
        realUser, 
        loading, 
        login, 
        register, 
        logout, 
        refresh, 
        setUser, 
        viewMode, 
        setViewMode, 
        isEmulating, 
        isStaff 
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
